import { useQuery, useMutation, QueryClient, useQueryClient } from '@tanstack/react-query';
import companiesAPI from '@global-apis/companies';
import Vendor from '@global-interfaces/Vendor';
import { graphqlClient, PaginatedResponse } from '@global-apis/config';
import Address from '@global-interfaces/Address';
import { gql, TypedDocumentNode } from '@apollo/client';
import { staleTimes, queryKeys } from '@root/constants/queryKeys';

type VendorUpdatePayload = Partial<Omit<Vendor, 'address'> & { address?: Partial<Address> }>;

export interface VendorData {
  id: number;
  name: string;
  email: string;
  address_id: number | null;
  bank_id: number | null;
  merged_into_id?: number | null;
  merged_at?: string | null;
  bank?: {
    account_number?: string | null;
    routing_number?: string | null;
    balance_id?: number | null;
    mercoa_id?: number | null;
  } | null;
}

interface VendorValueData {
  vendor: Vendor;
}

interface VendorsValueData {
  vendors: {
    results: VendorData[];
  };
}

export const GET_VENDOR_QUERY: TypedDocumentNode<VendorValueData> = gql`
  query GetVendor($id: Int!) {
    vendor(id: $id) {
      id
      name
      ein
      email
      phone
      cost_type
      qb_account_sub_type
      skip_sync_to_qb
      sage_id
      sage_available
      is_balance_configured
      company_id
      address {
        id
        address_1
        address_2
        city
        state
        zip_code
        country
        phone
      }
      address_id
      bank {
        id
        account_name
        account_type
        full_name
        institution_name
        verified
      }
      bank_id
      vendor_settings
    }
  }
`;

export const GET_VENDORS_QUERY: TypedDocumentNode<VendorsValueData> = gql`
  query GetVendors {
    vendors(include_merged: true) {
      results {
        id
        name
        email
        address_id
        bank_id
        merged_into_id
        merged_at
        bank {
          balance_id
          mercoa_id
        }
      }
    }
  }
`;

export const useVendorList = (options = {}) =>
  useQuery<VendorData[], Error>({
    queryKey: queryKeys.vendors,
    staleTime: staleTimes.masterData,
    select: (vendors) => vendors.filter((vendor) => !vendor.merged_into_id),
    queryFn: async () => {
      try {
        const { data } = await graphqlClient.query({
          query: GET_VENDORS_QUERY,
          fetchPolicy: 'network-only',
          // React Query owns cancellation/refetching; do not reuse a pre-activation Apollo request.
          context: { queryDeduplication: false }
        });

        return data.vendors.results;
      } catch (error) {
        // biome-ignore lint/suspicious/noConsole:
        console.error('Failed to fetch vendors:', error);
        throw error;
      }
    },
    ...options
  });

const updateVendorOnList = (queryClient: QueryClient, id: number, newVendor: Vendor) => {
  queryClient.setQueryData(queryKeys.vendors, (oldQueryData: VendorData[] | undefined) => {
    if (!oldQueryData) {
      return oldQueryData;
    }

    const index = oldQueryData.findIndex((item) => item.id === id);

    if (index === -1) {
      return oldQueryData;
    }

    const updatedItem = {
      ...oldQueryData[index],
      name: newVendor.name,
      email: newVendor.email ?? '',
      address_id: newVendor.address_id ?? null,
      bank_id: newVendor.bank_id ?? null
    };

    const updatedResults = [...oldQueryData];

    updatedResults[index] = updatedItem;

    return updatedResults;
  });
};

const addVendorToList = (queryClient: QueryClient, newVendor: Vendor, sortKey: keyof Vendor = 'name') => {
  queryClient.setQueryData(queryKeys.vendors, (oldQueryData: VendorData[] | undefined) => {
    if (!oldQueryData) {
      return [newVendor];
    }

    const updatedList = [...oldQueryData, newVendor];

    // Order by sortKey
    updatedList.sort((a, b) => {
      const aValue = a[sortKey];
      const bValue = b[sortKey];

      if (typeof aValue === 'string' && typeof bValue === 'string') {
        return aValue.localeCompare(bValue);
      }

      if (typeof aValue === 'number' && typeof bValue === 'number') {
        return aValue - bValue;
      }

      return 0;
    });

    return updatedList;
  });
};

const deleteVendorFromList = (queryClient: QueryClient, id: number) => {
  queryClient.setQueryData(queryKeys.vendors, (oldQueryData: VendorData[]) => {
    const updatedResults = oldQueryData.filter((item) => item.id !== id);

    return updatedResults;
  });
};

export const useVendorCreate = (options = {}) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: VendorUpdatePayload) => companiesAPI.vendor.create(data),
    onSuccess: ({ data }: { data: Vendor }) => {
      queryClient.setQueryData(queryKeys.vendor(data.id), data);
      addVendorToList(queryClient, data);
      queryClient.invalidateQueries({ queryKey: queryKeys.vendors });
    },
    ...options
  });
};

export const useVendorDelete = (options = {}) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: number) => {
      await companiesAPI.vendor.delete(id);

      return id;
    },
    onSuccess: (id: number) => {
      deleteVendorFromList(queryClient, id);
      queryClient.invalidateQueries({ queryKey: queryKeys.vendors });
    },
    ...options
  });
};

export const useVendorMerge = (options = {}) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ primaryId, secondaryId }: { primaryId: number; secondaryId: number }) => {
      await companiesAPI.vendor.merge(primaryId, secondaryId);

      return { primaryId, secondaryId };
    },
    onSuccess: ({ primaryId, secondaryId }: { primaryId: number; secondaryId: number }) => {
      queryClient.setQueryData<VendorData[]>(queryKeys.vendors, (vendors) =>
        vendors?.map((vendor) =>
          vendor.id === secondaryId
            ? { ...vendor, merged_into_id: primaryId, merged_at: new Date().toISOString() }
            : vendor
        )
      );
      void queryClient.invalidateQueries({ queryKey: queryKeys.vendors });
      void queryClient.invalidateQueries({ queryKey: queryKeys.vendor(primaryId) });
      void queryClient.invalidateQueries({ queryKey: queryKeys.vendor(secondaryId) });
      void queryClient.invalidateQueries({ queryKey: queryKeys.invoicesRoot });
      void queryClient.invalidateQueries({ queryKey: queryKeys.inboxReceived() });
    },
    ...options
  });
};

export const useVendorUpdate = (id: number, options = {}) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: VendorUpdatePayload) => companiesAPI.vendor.update(id, data),
    onSuccess: ({ data }: { data: Vendor }) => {
      updateVendorOnList(queryClient, id, data);
      queryClient.invalidateQueries({ queryKey: queryKeys.vendors });
      return queryClient.setQueryData(queryKeys.vendor(id), data);
    },
    ...options
  });
};

export const useVendor = (id: number | null, options = {}) => {
  const queryClient = useQueryClient();

  return useQuery<Vendor | null, Error>({
    queryKey: queryKeys.vendor(id),
    staleTime: staleTimes.referenceData,
    queryFn: async () => {
      if (!id) {
        return null;
      }

      const cachedVendor = queryClient.getQueryData<Vendor>(queryKeys.vendor(id));

      try {
        const vendor = await companiesAPI.vendor
          .fetch(id)
          .then(({ data }) => data)
          .catch(() => null);

        if (vendor?.id === id) {
          return vendor;
        }

        if (cachedVendor?.id === id) {
          return cachedVendor;
        }

        return null;
      } catch (error) {
        if (cachedVendor?.id === id) {
          return cachedVendor;
        }

        // biome-ignore lint/suspicious/noConsole:
        console.error('Failed to fetch vendor:', error);
        throw error;
      }
    },
    ...options
  });
};

export const fetchVendorLazy = async (queryClient: QueryClient, id: number) => {
  return await queryClient.fetchQuery({
    queryKey: queryKeys.vendor(id),
    queryFn: async () => {
      try {
        const { data } = await companiesAPI.vendor.fetch(id);
        updateVendorOnList(queryClient, id, data);
        return data;
      } catch (error) {
        // biome-ignore lint/suspicious/noConsole:
        console.error('Failed to fetch vendor:', error);
        throw error;
      }
    }
  });
};

export const getVendor = (queryClient: QueryClient, id: number) => {
  return queryClient.getQueryData<Vendor>(queryKeys.vendor(id));
};

export const vendorSearchUpdate = (
  queryClient: QueryClient,
  id: number | string,
  params: unknown,
  newData: Partial<Vendor>
) => {
  queryClient.setQueryData(queryKeys.vendorsSearch(params), (oldQueryData: PaginatedResponse<Vendor>) => {
    const index = oldQueryData?.results?.findIndex((item) => item.id === id);

    if (index === -1 || !oldQueryData?.results) {
      return oldQueryData;
    }

    const updatedItem: Vendor = { ...oldQueryData.results[index], ...newData };

    const updatedResults = [...oldQueryData.results];

    updatedResults[index] = updatedItem;

    return { ...oldQueryData, results: updatedResults };
  });
};

// BANK

export const cacheConfirmedVendorBank = (queryClient: QueryClient, vendorId: number, data: unknown) => {
  const bank = (data || {}) as NonNullable<Vendor['bank']>;
  const safeBank = Object.fromEntries(
    Object.entries({
      id: bank.id,
      account_name: bank.account_name,
      account_type: bank.account_type,
      institution_name: bank.institution_name,
      masked_account_number: bank.masked_account_number,
      masked_routing_number: bank.masked_routing_number,
      verified: bank.verified
    }).filter(([, value]) => value !== undefined)
  ) as NonNullable<Vendor['bank']>;

  // Cancel pre-activation snapshots before they can replace the confirmed data.
  void queryClient.cancelQueries({ queryKey: queryKeys.vendorBank(vendorId) });
  void queryClient.cancelQueries({ queryKey: queryKeys.vendor(vendorId) });
  void queryClient.cancelQueries({ queryKey: queryKeys.vendors });
  queryClient.setQueryData(queryKeys.vendorBank(vendorId), safeBank);
  if (typeof safeBank.id !== 'number') {
    return safeBank;
  }

  // Only confirmed activations reach here. Replace the old bank projection so
  // subsequent payments and AP indicators cannot use its stale eligibility/data.
  const update = { bank: safeBank, bank_id: safeBank.id, has_electronic_payment: true };
  queryClient.setQueryData<Vendor>(queryKeys.vendor(vendorId), (current) =>
    current ? { ...current, ...update } : current
  );
  queryClient.setQueriesData<VendorData[] | PaginatedResponse<Vendor>>({ queryKey: queryKeys.vendors }, (current) => {
    if (Array.isArray(current)) {
      return current.map((vendor) => (vendor.id === vendorId ? { ...vendor, ...update } : vendor));
    }
    if (current?.results) {
      return {
        ...current,
        results: current.results.map((vendor) => (vendor.id === vendorId ? { ...vendor, ...update } : vendor))
      };
    }
    return current;
  });
  // Resume canceled list refreshes; their snapshots may contain other vendor changes.
  void queryClient.invalidateQueries({ queryKey: queryKeys.vendors });
  void queryClient.refetchQueries({
    queryKey: queryKeys.vendor(vendorId),
    type: 'active',
    predicate: (query) => query.state.data === undefined
  });
  return safeBank;
};

export const useVendorBankFetch = (vendorId: number, options = {}) => {
  return useQuery({
    queryKey: queryKeys.vendorBank(vendorId),
    queryFn: async () => {
      try {
        return companiesAPI.vendor.bank.fetch(vendorId).then((res) => res.data);
      } catch (error) {
        // biome-ignore lint/suspicious/noConsole:
        console.error('Failed to fetch vendor bank:', error);
        throw error;
      }
    },
    ...options
  });
};

// ADDRESS

export const useVendorAddressFetch = (vendorId: number, options = {}) => {
  return useQuery<Address>({
    queryKey: queryKeys.vendorAddress(vendorId),
    queryFn: async () => {
      try {
        return companiesAPI.address.fetch(vendorId).then((res) => res.data);
      } catch (error) {
        // biome-ignore lint/suspicious/noConsole:
        console.error('Failed to fetch vendor address:', error);
        throw error;
      }
    },
    ...options
  });
};

export const vendorAddressUpdate = (queryClient: QueryClient, id: number, newData: Partial<Address>) => {
  queryClient.setQueryData(queryKeys.vendorAddress(id), (oldQueryData: Address) => {
    const updatedItem = { ...oldQueryData, ...newData };

    return updatedItem;
  });
};
